import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Shirt, Scissors, Sparkles, Palette } from "lucide-react";
import { ZODIAC, PHASES } from "@/lib/lunelle-data";
import { styleOfDay, styleForPhase, phaseStyle } from "@/lib/zodiac-style";
import { Reveal, MaskLines, Eyebrow } from "./Reveal";
import { useTodayPhase } from "@/lib/cycle";
import { useProfile } from "@/lib/account";

const Tip = ({ icon: Icon, title, text, testId }) => (
  <div className="border-t border-[#C86D51]/20 py-6">
    <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#9E4429]"><Icon className="h-4 w-4" />{title}</span>
    <p className="mt-2 font-serif text-2xl leading-snug text-[#2C221E]" data-testid={testId}>{text}</p>
  </div>
);

const SignChips = ({ sign, setSign }) => (
  <Reveal delay={0.1} className="mt-12 flex flex-wrap gap-2">
    {ZODIAC.map((x) => (
      <button key={x.id} onClick={() => setSign(x.id)} data-testid={`style-sign-${x.id}`}
        className={`relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${sign === x.id ? "text-[#FBF5EE]" : "border border-[#C86D51]/25 text-[#5C4A42] hover:border-[#9E4429]"}`}>
        {sign === x.id && <motion.span layoutId="style-pill" className="absolute inset-0 rounded-full bg-[#2C221E]" transition={{ type: "spring", stiffness: 300, damping: 30 }} />}
        <span className="relative">{x.name}</span>
      </button>
    ))}
  </Reveal>
);

const PhaseChips = ({ phase, setPhase, spaced }) => (
  <Reveal delay={0.15} className={`${spaced ? "mt-12" : "mt-5"} flex flex-wrap items-center gap-2`}>
    <span className="mr-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#7A665C]">My phase</span>
    {PHASES.map((x) => (
      <button key={x.id} onClick={() => setPhase(x.id)} data-testid={`style-phase-${x.id}`}
        className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-300 ${phase === x.id ? "text-[#FBF5EE]" : "border-[#C86D51]/25 text-[#5C4A42] hover:border-[#9E4429]"}`}
        style={phase === x.id ? { background: x.color, borderColor: x.color } : undefined}>
        {x.name}
      </button>
    ))}
  </Reveal>
);

export const ZodiacStyle = () => {
  const [sign, setSign] = useState("leo");
  const [phase, setPhase] = useState("follicular");
  const todayPhase = useTodayPhase()?.phase;
  const { member, zodiac: mySign, showZodiac, updateProfile } = useProfile();
  useEffect(() => {
    if (member && mySign) setSign(mySign);
  }, [member, mySign]);
  useEffect(() => {
    if (todayPhase) setPhase(todayPhase);
  }, [todayPhase]);
  const z = ZODIAC.find((x) => x.id === sign);
  const ph = PHASES.find((x) => x.id === phase);
  // Zodiac-off members get a full style of the day from their cycle phase
  const s = showZodiac ? styleOfDay(sign) : styleForPhase(phase);
  const look = phaseStyle(phase, s.colorOfDay[0]);
  return (
    <section id="style" className="relative py-28 sm:py-40" data-testid="zodiac-style-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <Reveal><Eyebrow>{showZodiac ? "04 — Written in the stars, worn by you" : "04 — Style of the day, from your cycle"}</Eyebrow></Reveal>
        <h2 className="mt-6 max-w-4xl font-serif text-5xl font-light leading-[1] tracking-tight text-[#2C221E] sm:text-6xl lg:text-7xl">
          <MaskLines key={String(showZodiac)} inView delay={0} lines={showZodiac
            ? ["Your sign,", <em key="e" className="italic text-[#9E4429]">styled for today.</em>]
            : ["Your phase,", <em key="e" className="italic text-[#9E4429]">styled for today.</em>]} />
        </h2>
        {showZodiac && <SignChips sign={sign} setSign={setSign} />}
        <PhaseChips phase={phase} setPhase={setPhase} spaced={!showZodiac} />

        <div className="mt-14 grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          <Reveal className="relative">
            <div className="relative aspect-[4/5] overflow-hidden rounded-t-[999px] rounded-b-[2rem]">
              <img src="/img/style.webp" loading="lazy" decoding="async" alt="Gold jewellery, sunglasses and a cream blazer" className="h-full w-full object-cover" />
              <motion.div key={s.colorOfDay[1]} initial={{ opacity: 0 }} animate={{ opacity: 0.28 }} transition={{ duration: 0.8 }}
                className="absolute inset-0 mix-blend-multiply" style={{ background: s.colorOfDay[1] }} />
            </div>
            <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-[#FDFBF7]/85 p-5 backdrop-blur-xl" data-testid="style-color-of-day">
              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#7A665C]"><Palette className="h-4 w-4" /> Colour of the day</span>
              <div className="mt-2 flex items-center gap-3">
                <span className="h-7 w-7 rounded-full ring-2 ring-white" style={{ background: s.colorOfDay[1] }} />
                <span className="font-serif text-2xl text-[#2C221E]" data-testid="style-color-name">{s.colorOfDay[0]}</span>
              </div>
            </div>
          </Reveal>

          <motion.div key={(showZodiac ? sign : "cycle") + phase} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
            {showZodiac ? (
              <>
                <p className="font-serif text-4xl text-[#2C221E]" data-testid="style-sign-name">{z.name}{member && mySign === sign && <span className="ml-3 align-middle text-xs font-semibold uppercase tracking-[0.2em] text-[#9E4429]" data-testid="style-my-sign-badge">Your sign</span>}</p>
                <p className="text-sm text-[#6B5A52]">{z.element} sign · {z.dates}</p>
              </>
            ) : (
              <>
                <p className="font-serif text-4xl text-[#2C221E]" data-testid="style-phase-name">{ph.season}</p>
                <p className="text-sm text-[#6B5A52]">{ph.name} phase · {look.vibe} days</p>
              </>
            )}
            <div className="mt-8 flex gap-3" data-testid="style-palette">
              {s.colors.map(([name, hex]) => (
                <div key={name} className="flex-1">
                  <div className="h-20 rounded-2xl sm:h-24" style={{ background: hex }} />
                  <p className="mt-2 text-xs font-medium text-[#5C4A42]">{name}</p>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Tip icon={Shirt} title={`Outfit of the day · ${look.vibe}`} text={s.outfit} testId="style-outfit" />
              <p className="-mt-3 pb-6 text-base leading-relaxed" style={{ color: ph.color }} data-testid="style-phase-tip">{look.tip}</p>
              <Tip icon={Scissors} title="Hair idea" text={s.hair} testId="style-hair" />
              <Tip icon={Sparkles} title="Beauty tip" text={s.beauty} testId="style-beauty" />
            </div>
            {!showZodiac && (
              <p className="mt-2 text-sm text-[#5C4A42]" data-testid="style-zodiac-off-note">Zodiac styling is off in your profile, so your look follows your cycle.{" "}
                <button type="button" onClick={() => updateProfile({ show_zodiac: true }).catch(() => {})} data-testid="style-zodiac-turn-on" className="font-semibold text-[#9E4429] underline underline-offset-2">Turn zodiac on</button>
              </p>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
